import type { LogLevel, LogSink } from './types';

// none < error < info < debug. A line is shown when its level is at or below the
// configured threshold — the same model the reference softphone uses, so log
// output is familiar across the two. `none` (threshold 0) sits below every real
// level, so nothing is ever emitted: the SDK is silent unless a dev opts in.
const LEVELS: Record<LogLevel, number> = { none: 0, error: 1, info: 2, debug: 3 };

const TAG = '[voice-sdk]';

type EmitLevel = 'error' | 'info' | 'debug';

export class Logger {
  private threshold: number;

  constructor(
    level: LogLevel = 'none',
    private sink?: LogSink,
  ) {
    this.threshold = LEVELS[level];
  }

  setLevel(level: LogLevel): void {
    this.threshold = LEVELS[level];
  }

  private write(level: EmitLevel, args: unknown[]): void {
    if (LEVELS[level] > this.threshold) return;
    // A custom sink takes over completely so the dev controls where lines go;
    // otherwise fall back to the browser console.
    if (this.sink) {
      try {
        this.sink(level, ...args);
      } catch {
        /* a throwing sink must not break the SDK */
      }
      return;
    }
    const out = level === 'error' ? console.error : level === 'debug' ? console.debug : console.info;
    out(TAG, ...args);
  }

  error(...args: unknown[]): void {
    this.write('error', args);
  }

  info(...args: unknown[]): void {
    this.write('info', args);
  }

  debug(...args: unknown[]): void {
    this.write('debug', args);
  }
}
